import { useState } from "react";
import type { GroupInfo, MaterialKind } from "../../../shared/api-contract";
import {
  addOption,
  groupsBySlot,
  patchOption,
  removeOption,
  setDefaultOption,
  slotKindOf,
  withSlotGroup,
  type ConfiguratorOption,
  type ConfiguratorOptionGroup,
  type OptionKind,
} from "../../../features/vault/configurator";
import { optionFromLibrary, type LibraryMaterial } from "../../../features/vault/applyOption";
import { useToast } from "../../../shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { loadKindOf } from "../../../state/editor/modelLoads";
import { notice, useDomainState } from "../../../state/editor/useEditor";
import { fixPartKind } from "../basic/kindFix";
import { useBasicEngine } from "../basic/useBasicEngine";
import { useConfiguratorDraft } from "./draft";
import { previewOption } from "./studioApply";

/**
 * The viewer's origin — the same value `app/ViewerHost.tsx` points the iframe
 * at. A library material file is fetched BY THE VIEWER, so a relative path in
 * the library means a path on this origin, and that is what a shared link must
 * be given in full.
 */
export const VIEWER_ORIGIN =
  (import.meta.env.VITE_VIEWER_ORIGIN as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5174";

/** A live slot: one viewer group and its position in `listGroups()` order. */
export interface LiveSlot {
  group: GroupInfo;
  index: number;
  /** The def group configuring it, if it has one yet. */
  config?: ConfiguratorOptionGroup;
  /**
   * What the slot IS — by its name, else as it loaded, else as it is now
   * (`slotKindOf`). Never "whatever it is wearing": that is `group.kind`,
   * and a pearl or a metal file on the stones flips it.
   */
  kind: OptionKind;
  /** The kind the viewer reported when the piece loaded, if recorded. */
  loadKind?: MaterialKind;
}

/** The slot is showing as the other kind (stones re-filed as metal, or the reverse). */
export const showsWrongKind = (slot: LiveSlot) => slot.group.kind !== slot.kind;

/**
 * Everything the canvas bar and the configurator drawer do to the draft, in
 * one place: add a library material to a part, try a choice on the model,
 * remove one. Trying a choice also makes it the one the part OPENS on — what
 * the designer is looking at when they save is what a shopper sees first.
 */
export function useConfiguratorEditing() {
  const [state, draft] = useConfiguratorDraft();
  const { client } = useViewer();
  const groups = (useDomainState("groups") as GroupInfo[] | undefined) ?? [];
  const engine = useBasicEngine();
  const { toast } = useToast();
  /** The option being put on the model right now. */
  const [busy, setBusy] = useState<string | null>(null);

  const byIndex = groupsBySlot(state.def, groups);
  const slots: LiveSlot[] = groups.map((group, index) => {
    const loadKind = loadKindOf(client, group.id);
    return { group, index, config: byIndex.get(index), loadKind, kind: slotKindOf(group, loadKind) };
  });

  /** Put `option` on the slot's part (one Undo step), remembering what the engine learned. */
  const show = async (slot: LiveSlot, groupId: string, option: ConfiguratorOption) => {
    setBusy(option.id);
    try {
      const result = await previewOption(engine, slot.group, option, slot.kind);
      if (result.skipped === "colourOnStone") {
        toast({
          tone: "warning",
          title: `${option.name} is a metal colour`,
          description: "Colour choices only show on metal parts, so the stones were left as they are.",
        });
      }
      if (result.format && option.preset) {
        const preset = { ...option.preset, format: result.format };
        draft.edit("groups", (d) => patchOption(d, groupId, option.id, { preset }));
      }
    } catch (e) {
      notice(`Couldn't show ${option.name}: ${e instanceof Error ? e.message : "the viewer refused it"}`);
    } finally {
      setBusy((b) => (b === option.id ? null : b));
    }
  };

  /** The library's material becomes a choice for the slot, and is shown. */
  const addFromLibrary = async (slot: LiveSlot, item: LibraryMaterial) => {
    const candidate = optionFromLibrary(item, { viewer: VIEWER_ORIGIN, page: window.location.origin });
    let groupId = "";
    let index = -1;
    draft.edit("groups", (d) => {
      const withGroup = withSlotGroup(d, { label: slot.group.label, kind: slot.group.kind, index: slot.index, loadKind: slot.loadKind }, groups);
      groupId = withGroup.groupId;
      const added = addOption(withGroup.def, groupId, candidate);
      index = added.index;
      return setDefaultOption(added.def, groupId, index);
    });
    // The stored copy, not the candidate: picking a material that is already a
    // choice re-shows that choice, with whatever was learned about it since.
    const stored = draft.getState().def.groups.find((g) => g.id === groupId)?.options[index];
    if (stored) await show(slot, groupId, stored);
  };

  /** Try a choice on the model; it becomes the one the part opens on. */
  const choose = async (slot: LiveSlot, optionId: string) => {
    const config = slot.config;
    const index = config?.options.findIndex((o) => o.id === optionId) ?? -1;
    if (!config || index < 0) return;
    draft.edit("groups", (d) => setDefaultOption(d, config.id, index));
    await show(slot, config.id, config.options[index]);
  };

  const remove = (slot: LiveSlot, optionId: string) => {
    const config = slot.config;
    if (config) draft.edit("groups", (d) => removeOption(d, config.id, optionId));
  };

  /**
   * Put a slot that is showing as the other kind back to what it is — the
   * "Is a part wrong?" switch's own step: reclassified, a clear diamond (or
   * white gold), ONE Undo step.
   */
  const fixKind = (slot: LiveSlot) =>
    fixPartKind(engine, slot.group, slot.kind).catch((e: Error) => notice(`Couldn't change that: ${e.message}`));

  return { state, draft, groups, slots, busy, engineBusy: engine.busy, addFromLibrary, choose, remove, fixKind };
}
